/* eslint-disable promise/always-return */
import 'jquery';
import publishService from 'tradera/apps/syi/services/publish-service';
import navigationService from 'tradera/apps/syi/services/navigation-service';
import draftService from 'tradera/apps/syi/services/draft-service';
import categoryActions from './category-actions';
import * as imageActions from './image-actions';
import priceActions from './price-actions';
import { shortDescriptionActions } from './short-description-actions';
import { setDescriptionLanguageCodeIso2 } from 'tradera/apps/syi/state/reducers/short-description';
import { setLongDescriptionFromDraft } from './long-description-actions';
import { getAndSetShippingOptionsByWeight } from './shipping-actions';
import {
    resetSelectedShippingOptions,
    setShippingFromCountry
} from '../reducers/shipping/shipping-reducer';
import campaignCodeActions from './campaign-code-actions';
import {
    resetCharityAndShippingInfo,
    setCharityAndFetchInfo,
    setCharityFromDraftAndFetchInfo
} from 'tradera/apps/syi/state/actions/charity-actions';
import feesActions from './fees-actions';
import { setAttributesFromDraft } from './attributes-actions';
import scribe from 'tradera/apps/syi/services/scribe';
import {
    setAllowedBuyerRegion,
    setAvailableAllowedBuyerRegions
} from '../reducers/buyer-region';
import { resetImageAttributeSuggestions } from '../reducers/attributes';
import { handleServerValidationError } from './helpers/server-validation-error-helper';
import { getSyiSlice } from 'tradera/apps/syi/state/selectors/syi';
import {
    selectCategorySuggestedAttributes,
    selectImageToColorSuggestedAttributes
} from 'tradera/apps/syi/state/selectors/attribute-selectors';
import {
    selectRecommendedWeight,
    selectShippingRecommendations
} from 'tradera/apps/syi/state/selectors/shipping-selectors';
import { selectFeatureSwitches } from 'tradera/state/environment/selectors';
import {
    trackAttributeSuggestions,
    trackShippingRecommendations
} from './draft-tracking';
import { logger } from 'packages/logger';
import dayjs from 'dayjs';
import {
    getDefaultSelectedDescriptionLanguageCodeIso2,
    getDefaultShippingFromCountryCodeIso2
} from 'tradera/apps/my-tradera/app/components/settings/selling/item-localization';
import userData from 'tradera/apps/syi/services/user-data';
import initData from 'init-data';
import { Tags, util } from '../drafts-api';
import { setCustomEndTime, setDurationDays } from '../reducers/item-duration';
import { GtmService as gtm } from 'tradera/services/google-tagmanager-service';
import {
    disableAutoSave,
    enableAutoSave,
    hideAllErrors,
    resetSyi,
    setDraftCanBeResumed,
    setIsEditOfPublishedItem,
    setPublishFailed,
    setPublishPending,
    toggleModal
} from '../reducers/syi';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';
import {
    addAdditionalDescription,
    setCurrency,
    setDraftId,
    setDraftIsLoading,
    setDraftProperties,
    setDraftReason,
    setDraftVersion,
    setMagicalSyiEnabled,
    setResellPartner
} from 'tradera/apps/syi/state/reducers/draft';
import { memberApi } from 'tradera/state/services/member-api';
import { trackSyiForm } from 'tradera/apps/syi/services/syi-ga4-service';
import {
    selectIsItemConditionDirty,
    selectItemCondition
} from 'tradera/apps/syi/state/selectors/item-condition-selectors';
import { setHasUsedAutoFill } from 'tradera/apps/syi/state/reducers/predictions-reducer';

function ensureDraftReason(draft) {
    if (!draft.draftReason) {
        // eslint-disable-next-line better-mutation/no-mutation
        draft.draftReason = 'NewItem';
    }
}

const trackAddonBeforePublish = function (addOnfeatureName) {
    gtm.trackAction('Listing AddOns', addOnfeatureName, 'Before Publish');
};

const setDraft =
    (draft, params = {}) =>
    (dispatch, getState) => {
        const memberCountryCodeIso2 =
            selectShippingRegionCountryCodeIso2(getState());

        const fromCountry =
            draft.fromCountry ||
            userData.shippingFromCountryCodeIso2 ||
            getDefaultShippingFromCountryCodeIso2(memberCountryCodeIso2);

        dispatch(setShippingFromCountry({ fromCountry }));

        dispatch(disableAutoSave());
        ensureDraftReason(draft);

        const isNewDraft =
            getSyiSlice(getState()).draft.draftId != draft.draftId ||
            getSyiSlice(getState()).draft.draftReason != draft.draftReason;
        const isChangeItem = draft.draftReason === 'ChangeItem';

        // Do first for actions that depends on this info.
        dispatch(setIsEditOfPublishedItem(isChangeItem));
        dispatch(hideAllErrors());

        dispatch(setDraftProperties(draft));

        dispatch(
            setAllowedBuyerRegion({
                allowedBuyerRegion: draft.allowedBuyerRegion
            })
        );
        dispatch(setAvailableAllowedBuyerRegions());
        // fallback to null to not become dirty if undefined
        dispatch(setItemConditionFromUser(draft.itemCondition ?? null));
        dispatch(
            campaignCodeActions.setInitialCampaignCode(
                draft.campaignCode,
                draft.campaignCodeId,
                draft.campaignCodeDescription,
                isChangeItem
            )
        );

        // Setting short description also loads top categories!
        dispatch(
            shortDescriptionActions.setShortDescription(draft.shortDescription)
        );
        dispatch(
            setDescriptionLanguageCodeIso2(
                draft.descriptionLanguageCodeIso2 ||
                    userData.selectedDescriptionLanguageCodeIso2 ||
                    getDefaultSelectedDescriptionLanguageCodeIso2(
                        initData.languageCodeIso2,
                        memberCountryCodeIso2
                    )
            )
        );
        dispatch(setCurrency(draft.currency));
        dispatch(setResellPartner(draft.resellPartner));
        dispatch(
            categoryActions.setDraftCategory(
                draft.categoryId,
                draft.categoryPath
            )
        );
        dispatch(
            setDurationDays({ durationDays: draft.durationDays, isChangeItem })
        );
        dispatch(
            setCustomEndTime({
                customEndTime: draft.customEndTime,
                isChangeItem
            })
        );
        dispatch(setLongDescriptionFromDraft(draft.longDescription));
        dispatch(setDisplayPromoted(draft.displayPromoted));
        dispatch(setDisplayHighlighted(draft.displayHighlighted));
        dispatch(imageActions.setDraftImages(draft.images || []));
        dispatch(resetImageAttributeSuggestions());
        dispatch(
            priceActions.setFromDraft(
                draft.openingBid,
                draft.fixedPrice,
                draft.reservePrice,
                draft.charity?.charityId,
                draft.vat,
                isChangeItem
            )
        );

        const pennybridgeCharitiesEnabled =
            selectFeatureSwitches(getState())['pennybridge-charities'];

        if (pennybridgeCharitiesEnabled) {
            if (params.charityId) {
                dispatch(setCharityAndFetchInfo(params.charityId));
            } else if (draft.charity) {
                dispatch(setCharityFromDraftAndFetchInfo(draft.charity));
            }
        }
        dispatch(setAttributesFromDraft(draft));

        dispatch(getAndSetShippingOptionsByWeight(draft.shippingOptions));

        dispatch(enableAutoSave());

        dispatch(memberApi.endpoints.getMagicalSyiEnabled.initiate())
            .then((result) => {
                if (result.data) {
                    dispatch(setMagicalSyiEnabled(result.data.enabled));
                }
            })
            .catch(() => {
                // Do nothing
            });

        if (isNewDraft) {
            scribe.sendSessionStart();
        }
    };

const loadDraft = (dispatch, draftId) => {
    dispatch(setDraftIsLoading(true));
    return draftService
        .loadDraft(draftId)
        .then((result) => {
            dispatch(setDraft(result));
            return result;
        })
        .catch((result) => {
            navigationService.goToStart();
            throw result;
        })
        .finally(() => dispatch(setDraftIsLoading(false)));
};

const loadImagesFromDraft = (draftId) => async (dispatch, getState) => {
    const browserDraftImageUrls = (getSyiSlice(getState()).images.images || [])
        .map((image) => image.imageUrl)
        .filter(Boolean);

    const serverDraft = await draftService.loadDraft(draftId);
    const serverDraftImages = serverDraft.images || [];
    const serverDraftImageUrls = serverDraftImages.map(
        (image) => image.imageUrl
    );

    const changedImages = browserDraftImageUrls
        .filter((x) => !serverDraftImageUrls.includes(x))
        .concat(
            serverDraftImageUrls.filter(
                (x) => !browserDraftImageUrls.includes(x)
            )
        );
    const imagesHaveChanged = changedImages.length > 0;
    if (imagesHaveChanged) {
        dispatch(imageActions.setDraftImages(serverDraftImages));
    }
    return imagesHaveChanged;
};

// KATS: this should perhaps be moved to the draft service on BE - do not save a draft if it's not
// complete. Then we could check in FE if the save action succeeded, and if it did then we know that it can be resumed.
export const canBeResumed = (
    draftId,
    draftReason,
    shortDescription,
    longDescription,
    images
) => {
    if (!draftId) {
        return false;
    }

    if (draftReason !== 'NewItem') {
        return false;
    }

    var hasShortDescription =
        shortDescription && shortDescription.trim().length > 0;
    var hasLongDescription =
        longDescription && longDescription.trim().length > 0;
    var hasImage =
        Array.isArray(images) && images.some((image) => !!image.imageUrl);
    return hasShortDescription || hasLongDescription || hasImage;
};

const updateDraftCanBeResumed = () => (dispatch, getState) => {
    const draft = getSyiSlice(getState()).draft;
    const shortDescription =
        getSyiSlice(getState()).shortDescription.shortDescription;
    const longDescription =
        getSyiSlice(getState()).longDescription.longDescription;
    const images = getSyiSlice(getState()).images.images;

    // TODO: avoid duplication by using canBeResumed
    if (!draft.draftId) {
        return false;
    }

    if (draft.draftReason !== 'NewItem') {
        return false;
    }

    var hasShortDescription =
        shortDescription && shortDescription.trim().length > 0;
    var hasLongDescription =
        longDescription && longDescription.trim().length > 0;
    var hasImage = Array.isArray(images) && images.length > 0;
    const canBeResumed = hasShortDescription || hasLongDescription || hasImage;

    dispatch(setDraftCanBeResumed(canBeResumed));
};

const setDisplayPromoted = (displayPromoted) => (dispatch) => {
    dispatch({
        type: 'SET_DISPLAY_PROMOTED',
        payload: displayPromoted || false
    });
    dispatch(feesActions.updatePrices());
};

const setDisplayHighlighted = (displayHighlighted) => (dispatch) => {
    dispatch({
        type: 'SET_DISPLAY_HIGHLIGHTED',
        payload: displayHighlighted || false
    });
    dispatch(feesActions.updatePrices());
};

const publish = (t, doneCallback) => (dispatch, getState) => {
    dispatch(setPublishPending());

    const state = getSyiSlice(getState());
    const { displayHighlighted, displayPromoted } = state.extraFeatures;
    const reservePrice = state.price.reservePrice;
    const draft = draftService.buildDraftFromState(state);
    var splitTestGroups = getState().environment.splitTestGroups;
    var featureSwitches = getState().environment.featureSwitches;
    const customEndTime = state.itemDuration.customEndTime;

    if (draft.draftReason === 'NewItem') {
        gtm.trackAction(
            'SYI',
            'Creation time in seconds',
            String(dayjs().diff(dayjs(state.syi.creationStart), 'second'))
        );
    }

    publishService
        .publish(draft, state.memberConsent.hasTraderaPayConsent)
        .then((result) => {
            if (displayHighlighted) {
                trackAddonBeforePublish('Highlight');
            }
            if (displayPromoted) {
                trackAddonBeforePublish('Rank up');
            }
            if (reservePrice) {
                trackAddonBeforePublish('Reserve price');
            }
            if (customEndTime) {
                trackAddonBeforePublish('End time');
            }

            if (featureSwitches['syi-attribute-suggestions']) {
                const draftSelectedCategoryAttributes =
                    draft.attributeValues.termAttributeValues;
                const suggestedCategoryAttributes =
                    selectCategorySuggestedAttributes(getState()) || [];
                if (draftSelectedCategoryAttributes.length > 0) {
                    trackAttributeSuggestions(
                        draftSelectedCategoryAttributes,
                        suggestedCategoryAttributes,
                        splitTestGroups['attribute_suggestion_extractor'],
                        state.draft.isNewDraft,
                        result.itemId
                    );
                }
            }

            if (featureSwitches['syi-image-attribute-suggestions']) {
                const draftSelectedColorAttributes =
                    draft.attributeValues.termAttributeValues.filter(
                        (a) => a.backingField === 'color'
                    );
                const suggestedColorAttributes =
                    selectImageToColorSuggestedAttributes(getState()) || [];
                if (draftSelectedColorAttributes) {
                    trackAttributeSuggestions(
                        draftSelectedColorAttributes,
                        suggestedColorAttributes,
                        'ImageToColor',
                        state.draft.isNewDraft,
                        result.itemId
                    );
                }
            }

            if (featureSwitches['shipping-recommendations']) {
                trackShippingRecommendations(
                    draft.categoryId,
                    draft.shippingOptions,
                    selectRecommendedWeight(getState()) || 0,
                    selectShippingRecommendations(getState()) || [],
                    result.itemId
                );
            }

            const { predictions, shortDescription, longDescription } = state;
            const hasAiTitle =
                predictions.shortDescription ===
                shortDescription.shortDescription;
            const hasAiDescription =
                predictions.longDescription === longDescription.longDescription;

            trackSyiForm({
                eventName: 'syi_form_submit_success',
                parameters: {
                    form_name: 'syi_draft',
                    item_id: result.itemId,
                    ai_title: hasAiTitle ? 1 : 0,
                    ai_description: hasAiDescription ? 1 : 0,
                    ai_images: predictions.numProcessedImages,
                    ai_processing_time: predictions.aiProcessingTime,
                    draft_id: draft.draftId
                }
            });

            doneCallback?.(result.itemId);

            dispatch(setDraft({}));
            navigationService.goToPublished(result.itemId);
        })
        .catch((response) => {
            logger('Failed to publish SYI draft', {
                contexts: {
                    syiPublish: response
                }
            });

            if (
                hasResponseErrorCode(response, [
                    'DraftAlreadyPublished',
                    'DraftBelongsToAnotherMember'
                ])
            ) {
                navigationService.goToStart();
                dispatch(setDraft({}));
            } else if (hasResponseErrorCode(response, ['ValidationError'])) {
                handleServerValidationError(response.errors || [], dispatch, t);
            } else {
                dispatch(setPublishFailed());
                dispatch(toggleModal('error-modal-publish-failed'));
            }
        });
};

const hasResponseErrorCode = (responseOrNull, arrayOfErrorCodesToCheck) =>
    responseOrNull &&
    arrayOfErrorCodesToCheck.includes(responseOrNull.errorCode);

const loadDraftIfMissing = (draftId) => (dispatch, getState) => {
    var draft = getSyiSlice(getState()).draft;
    const isAlreadyLoaded = draft.draftId === draftId;
    if (isAlreadyLoaded) {
        return Promise.resolve(draft);
    }
    return loadDraft(dispatch, draftId);
};

const loadDefaultDraft =
    ({ charityId }) =>
    (dispatch) => {
        dispatch(resetSyi());
        dispatch(resetCharityAndShippingInfo());
        dispatch(setDraft(draftService.loadDefaultDraft(), { charityId }));
        dispatch(resetSelectedShippingOptions());
    };

const loadDefaultTemplateDraft = () => (dispatch) => {
    dispatch(resetSyi());
    dispatch(resetCharityAndShippingInfo());
    var template = draftService.loadDefaultTemplateDraft();
    dispatch(setDraft({ ...template, templateId: 'new' }));
};

const useDraftAsTemplate = () => (dispatch) => {
    dispatch(setDraftReason('Template'));
    dispatch(setDraftId(null));
};

export const triggerSave = () => (dispatch) => dispatch(saveDraft);

const saveDraft = (dispatch, getState) => {
    const draft = draftService.buildDraftFromState(getSyiSlice(getState()));
    const version = getSyiSlice(getState()).draft.version || 0;
    return draftService
        .saveDraft(draft)
        .then((result) => {
            if (draft.draftId !== result.draftId) {
                dispatch(setDraftId(result.draftId));
            }
            if (version > 0 && version + 1 !== result.version) {
                // Unexpected version. The draft is being edited somewhere else.
            }
            const wasNewDraftWithoutId = !draft.draftId && !!result.draftId;
            // since this function runs on a timer, we might have moved to a new route.
            // For example, when publishing a draft, we move to the published page, but
            // this function might run afterwards, which creates a new draft. Therefore,
            // make sure to only run this if we are still on the 'new' route. TWT-6947
            const routeIsNew = window.location.href.endsWith('/new');
            if (wasNewDraftWithoutId && routeIsNew) {
                navigationService.setDraftUrlWithoutReload(result.draftId);
            }
            // Must happen after new URL is set for re-render to happen.
            dispatch(setDraftVersion(result.version));
            // KATS: This is only manually dispatched since not all of the templates API is in RTK query yet
            dispatch(util.invalidateTags([Tags.DRAFT_LIST]));

            return result;
        })
        .catch((result) => {
            const response = result.responseJSON;

            logger('Failed to save SYI draft', {
                contexts: {
                    syiSave: response
                }
            });

            if (
                hasResponseErrorCode(response, [
                    'DraftAlreadyPublished',
                    'DraftBelongsToAnotherMember'
                ])
            ) {
                dispatch(setDraft({}));
                navigationService.goToStart();
            } else if (result.status == 404) {
                dispatch(setDraft({}));
                navigationService.goToStart();
            }
            throw result;
        });
};

const setUsedAllowed = (isAllowed) => ({
    type: 'SET_ITEM_CONDITION_SELECTION_ERROR',
    payload: isAllowed ? null : 'syi_validation_used_not_allowed_for_category'
});

const setItemConditionFromUser = (condition) => (dispatch) => {
    dispatch({
        type: 'SET_ITEM_CONDITION_FROM_USER',
        payload: condition
    });
    dispatch(updateItemConditionRestrictionForCategory());
};

const setItemConditionFromPrediction = (condition) => (dispatch, getState) => {
    const isPristine = !selectIsItemConditionDirty(getState());
    if (!isPristine) {
        return;
    }
    const oldValue = selectItemCondition(getState());

    dispatch({
        type: 'SET_ITEM_CONDITION',
        payload: condition
    });

    if (oldValue !== condition) {
        dispatch(setHasUsedAutoFill(true));
    }

    dispatch(updateItemConditionRestrictionForCategory());
};

const updateItemConditionRestrictionForCategory =
    () => (dispatch, getState) => {
        const condition = getSyiSlice(getState()).itemCondition.itemCondition;
        const isUsed = condition === 'Used';
        if (isUsed) {
            const isAllowed = isUsedAllowed(getState);
            dispatch(setUsedAllowed(isAllowed));
        } else {
            dispatch(setUsedAllowed(true));
        }
    };

const isUsedAllowed = (getState) => {
    const categoryRestriction = getSyiSlice(getState()).syi.categoryRestriction;
    return !categoryRestriction || categoryRestriction.allowUsed !== false;
};

const resetItemCondition = () => ({
    type: 'RESET_ITEM_CONDITION'
});

const publishAdditionalDescription = (text) => (dispatch, getState) => {
    if (text && text.length > 0) {
        dispatch(addAdditionalDescription(text));
    }
    dispatch(setPublishPending());
    const draft = draftService.buildDraftFromState(getSyiSlice(getState()));
    publishService
        .publish(draft)
        .then((result) => {
            navigationService.goToPublished(result.itemId);
            dispatch(setDraft({}));
        })
        .catch(() => {
            dispatch(setPublishFailed());
        });
};

export default {
    setDraft,
    loadDraft,
    loadImagesFromDraft,
    canBeResumed,
    updateDraftCanBeResumed,
    setDisplayPromoted,
    setDisplayHighlighted,
    publish,
    loadDraftIfMissing,
    loadDefaultDraft,
    loadDefaultTemplateDraft,
    useDraftAsTemplate,
    saveDraft,
    setItemConditionFromPrediction,
    setItemConditionFromUser,
    resetItemCondition,
    publishAdditionalDescription,
    updateItemConditionRestrictionForCategory
};
